import type { FaqItem, InternalLink, SizingRow } from './types';

export interface GuideContent {
  slug: string;
  title: string;
  description: string;
  h1: string;
  lede: string;
  answer: string;
  updated: string;
  sections: { heading: string; paragraphs?: string[]; bullets?: string[] }[];
  specTable: SizingRow[];
  checklist: string[];
  faqs: FaqItem[];
  related: InternalLink[];
}

export const hospitalityGuide: GuideContent = {
  slug: 'best-infrared-heaters-for-hospitality-2026',
  title: 'Best Infrared Heaters for Hospitality (2026 Guide)',
  description:
    'A 2026 specification guide for hospitality infrared heating: output per m², mounting height, wind, controls, certification by market, spares and tender checklist.',
  h1: 'How to specify infrared heaters for hospitality in 2026',
  lede:
    'A buyer’s guide written for the person who has to keep the terrace open in February — not for a brochure.',
  answer:
    'The best infrared heater for a hospitality venue is the one that reaches occupant comfort at the lowest running cost with parts you can still buy in five years. That means 80–160 W per m² depending on exposure, mounting at 1.8–2.5 m, IPX4 for outdoor positions, certified supply for your market, and a spares commitment from the supplier — in that order.',
  updated: 'November 2026',
  sections: [
    {
      heading: 'What “best” should mean for a venue',
      paragraphs: [
        'Consumer reviews rank heaters by price and looks. A venue has different criteria: it will run the unit for six hours a day through winter, it will be cleaned by staff who did not install it, and it needs a replacement element in three years without importing a new fixture.',
        'So the useful ranking criteria are duty cycle, serviceability, control granularity and certification — not the number on the box. A 3.2 kW heater with no spare-part supply is worse value than a 2.4 kW heater you can repair.',
      ],
      bullets: [
        'Duty cycle: can it run continuously for a full service, not just an evening?',
        'Serviceability: is the element replaceable, and at what price and lead time?',
        'Control granularity: can you run half the terrace without running the whole circuit?',
        'Certification: does the unit carry the mark your market requires, with documents?',
        'Fixings and IP rating: does the mounting hardware suit the wall, and is IPX4 enough for the position?',
      ],
    },
    {
      heading: 'How much output does a terrace actually need?',
      paragraphs: [
        'The industry shorthand is watts per square metre, and it works well enough to start a conversation. A sheltered, glazed terrace needs roughly 80–100 W/m². A terrace with one or two open sides needs 110–130 W/m². An exposed rooftop or a site with a prevailing wind needs 130–160 W/m², and a windbreak will do more good than another kilowatt.',
        'Mounting height changes the calculation. Radiant intensity falls with distance, so a heater at 2.5 m delivers materially less to a seated guest than the same unit at 2.0 m. In spaces with ceilings above 3 m, plan on higher output per fixture or accept coverage of the seating cluster rather than the whole floor.',
      ],
    },
    {
      heading: 'Wind is a layout problem, not a wattage problem',
      paragraphs: [
        'Every experienced operator has seen a terrace with four heaters running and guests still in coats. Almost always the heaters are fighting air movement that a screen would have stopped. Glass panels, planter walls, awning sides and pergola infills cost less per square metre of comfort than additional heaters, and they reduce running cost permanently.',
        'Place heaters on the leeward side of the seating, facing across it. A heater mounted on the windward wall blows its radiant field into moving air; the same heater mounted opposite works.',
      ],
    },
    {
      heading: 'Certification by market',
      bullets: [
        'European Union / EEA: CE marking, with EMC and safety documentation; RoHS for materials.',
        'United Kingdom: check the current UKCA position with your importer — CE and UKCA requirements have diverged.',
        'Australia and New Zealand: SAA certification is the usual expectation for electrical appliances.',
        'Saudi Arabia and the Gulf: SASO for market access; confirm any additional efficiency labelling.',
        'China: CCC for domestic sale.',
        'Every market: ask for the certificate, not the logo. A declaration of conformity without a test report is not a specification.',
      ],
    },
    {
      heading: 'The five mistakes we see most often',
      bullets: [
        'Sizing on floor area when only a third of the floor is seating.',
        'Buying the highest wattage available instead of fixing the wind.',
        'Single-circuit installation, so the whole terrace runs for four guests.',
        'Forgetting that 3.2 kW draws 13.9 A and a four-heater terrace needs a proper distribution board.',
        'No spare-element plan, then discovering the model is discontinued two winters later.',
      ],
    },
  ],
  specTable: [
    { space: 'Sheltered terrace / glazed', area: '10–20 m²', power: '1.0–1.8 kW', model: '13A / 13B / 13W', note: '80–100 W/m²' },
    { space: 'Semi-open terrace', area: '20–30 m²', power: '2.2–2.4 kW', model: '22B / 13C', note: '110–130 W/m²' },
    { space: 'Open frontage', area: '30–40 m²', power: '3.2 kW', model: '22B / 13C', note: '130–160 W/m²' },
    { space: 'Large courtyard / hall', area: '50 m²', power: '4.0 kW', model: '13A / 14A (380 V)', note: 'Three-phase supply' },
    { space: 'Workstation spot heat', area: 'per position', power: '1.0–2.4 kW', model: '13A / 13B / 13C', note: 'Mount above the bench' },
  ],
  checklist: [
    'Measured seated area, with open sides marked on a plan',
    'Prevailing wind direction at the venue',
    'Mounting surfaces: wall, column, soffit or beam, with fixing method agreed',
    'Available electrical capacity and whether three-phase reaches the terrace',
    'Zoning plan: which heaters switch together, and who controls them',
    'Destination market and required certification documents',
    'IP rating appropriate to the position (IPX4 minimum for exposed)',
    'Spare element quantity and price included in the quotation',
    'Warranty terms for commercial use, in writing',
    'Delivery schedule against the season opening date',
  ],
  faqs: [
    {
      q: 'What is the best infrared heater for a restaurant terrace?',
      a: 'For most restaurant terraces, a 2.2–3.2 kW wall-mounted far-infrared heater at 1.8–2.5 m, IPX4 rated, with zoned switching. The specific model matters less than the layout: coverage, wind protection and zoning decide whether guests are comfortable.',
    },
    {
      q: 'How many watts per square metre for outdoor heating?',
      a: 'Use 80–100 W/m² for a sheltered terrace, 110–130 W/m² with one or two open sides, and 130–160 W/m² for exposed rooftops. These are planning figures; wind, mounting height and glazing move the real number.',
    },
    {
      q: 'Is gas or electric cheaper to run for a patio?',
      a: 'Electric radiant heat is directional and can be zoned per seating cluster, which usually makes it cheaper to run for partial occupancy. Gas units have higher raw output but heat a wider, less controllable area and require fuel handling and ventilation.',
    },
    {
      q: 'How long do infrared heaters last in a commercial setting?',
      a: 'The housing typically outlasts the element. Plan on element replacement as normal maintenance rather than failure, and choose a supplier who can still ship that element in year five. Salt-air and high-use sites need a documented inspection routine.',
    },
    {
      q: 'Do I need a specialist installer?',
      a: 'The heater itself mounts like a light fitting, but the circuit must be installed and certified by a qualified electrician. Provide them with the load schedule and the bracket drawings, both of which we supply with the quotation.',
    },
    {
      q: 'Can one supplier cover several sites in different countries?',
      a: 'That is the case for a factory programme rather than a retail purchase: one specification, one spare-part list and one certification pack across the estate. Configurations differ by market voltage and plug, which we handle at order stage.',
    },
  ],
  related: [
    { label: 'Restaurants and cafés', href: '/business/restaurants-cafes' },
    { label: 'Hotel terraces', href: '/business/hotels-terraces' },
    { label: 'Rooftop bars', href: '/business/rooftop-bars' },
    { label: 'Halls and worship spaces', href: '/business/places-of-worship-event-halls' },
    { label: 'Sizing calculator', href: '/tools/heater-sizing-calculator' },
    { label: 'All infrared heaters', href: '/infrared-heaters' },
  ],
};

export const GUIDE_PATH = `/guides/${hospitalityGuide.slug}`;
